import { useEffect, useMemo, useState } from 'react';
import type { RoleMapCoverage } from '@/api/console';
import type { RoleCoverageData } from './useRoleCoverageData';
import { computeAppCoverageRows, countByTab, matchesSearch, matchesTab } from './coverageHelpers';
import type { AppCoverageRow, CoverageTab, MatrixCell } from './types';

/** One request to move the editor's focus to a role row — a nonce (not just
 *  the role key) so clicking the SAME cell twice still re-triggers the
 *  scroll/focus effect in `RoleEditorCard`. */
export interface FocusRequest {
    roleKey: string;
    nonce: number;
}

/**
 * EAA-1817: the left-rail list + the matrix card's selection state — "which
 * app is open in the editor" and "which cell was last clicked" (so the
 * editor can scroll/focus that role's row per the design's "Nhấp một ô để mở
 * đúng app + vai trò đó").
 */
export function useCoverageBoard(data: RoleCoverageData) {
    const { coverageQuery, adminAppsQuery } = data;

    const [search, setSearch] = useState('');
    const [tab, setTab] = useState<CoverageTab>('all');
    const [selectedAppCode, setSelectedAppCode] = useState<string | null>(null);
    const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);

    const apps = useMemo(() => adminAppsQuery.data ?? [], [adminAppsQuery.data]);
    const coverage: RoleMapCoverage | undefined = coverageQuery.data;

    const appRows = useMemo<AppCoverageRow[]>(() => {
        if (!coverage) return [];
        return computeAppCoverageRows(apps, coverage);
    }, [apps, coverage]);

    // EAA-1817: pick a default selection once the rows resolve, so the
    // editor card is never blank on first load — mirrors the design frame,
    // which always shows one app already open. Never overrides a caller's
    // own click.
    useEffect(() => {
        if (selectedAppCode !== null) return;
        if (appRows.length === 0) return;
        setSelectedAppCode(appRows[0].code);
    }, [appRows, selectedAppCode]);

    const normalizedSearch = search.trim().toLowerCase();
    const visibleRows = useMemo(
        () => appRows.filter((row) => matchesSearch(row, normalizedSearch) && matchesTab(row, tab)),
        [appRows, normalizedSearch, tab]
    );

    const needsAttentionCount = useMemo(() => countByTab(appRows, 'needsAttention'), [appRows]);
    const uncoveredCount = useMemo(() => countByTab(appRows, 'uncovered'), [appRows]);

    const selectedAppRow = useMemo(
        () => appRows.find((row) => row.code === selectedAppCode) ?? null,
        [appRows, selectedAppCode]
    );

    /* EAA-1817: `matrixRows` used to be derived here — one row per application,
       the role axis as columns. It moved to `coverageMatrixHelpers.ts` and came
       back TRANSPOSED (roles down, apps across) as design frame 55a specifies;
       see that file's docstring for why the orientation is part of the spec.
       Nothing on this hook needs a grid any more: the board it backs is the
       rail + per-app editor of frame 55. */

    function selectCell(cell: MatrixCell) {
        setSelectedAppCode(cell.appCode);
        setFocusRequest((prev) => ({ roleKey: cell.roleKey, nonce: (prev?.nonce ?? 0) + 1 }));
    }

    function selectApp(appCode: string) {
        setSelectedAppCode(appCode);
    }

    return {
        search,
        setSearch,
        tab,
        setTab,
        appRows,
        visibleRows,
        needsAttentionCount,
        uncoveredCount,
        selectedAppCode,
        selectedAppRow,
        selectApp,
        roleKeys: coverage?.role_keys ?? [],
        focusRequest,
        selectCell,
    };
}

export type CoverageBoardVM = ReturnType<typeof useCoverageBoard>;
